import { Status } from "@/types/Status"
import { Dayjs } from "dayjs"
import { SoCarouselItem } from "@/components/SoCarousel.vue"

export interface IMeterAppointment {
  id: number
  slotStartDateTime: Dayjs
  slotEndDateTime: Dayjs
  mpan: string
  mprn: string
  engineerName: string
  engineerEmail: string
  appointmentId: string
  electricMeterSerialNumber: string
  gasMeterSerialNumber: string
  /** Customer-facing job number — the only one that should ever be displayed. */
  displayJobNumber: string
  aesJobType: string
  aesStatus: MeterAppointmentStatus | null
  cancelledDateTime?: Dayjs
  installedDateTime?: Dayjs
  failedDateTime?: Dayjs
  appointmentType: string
  meterType: string
  installationType: string
  timeSlot: string
  createdAt: Dayjs
  survey?: IMeterAppointmentSurvey[]
  history?: IMeterAppointment[]
  events?: IMeterAppointmentEvent[]
  comments?: IMeterAppointmentComment[]
  abortReason?: string
  cancelReason?: string
  mop?: string

  readonly status: Status
  readonly installer: string
  readonly engineerDetails: string
  readonly slotDate: string
  readonly timeSlotRange: string
  readonly title: string
  readonly notes: IMeterAppointmentSurvey[]
  readonly description: string
  readonly abortImageFromSurvey: string | null
  readonly surveyImages: SoCarouselItem[]
  readonly parsedAbortReason: IAbortReasonEntry[]
  readonly removedMeterReads: IMeterAppointmentSurvey[]
}

export interface IMeterAppointmentSurvey {
  name: string
  groupName: string
  question: string
  answer: string
  imageUrl: string
}

export interface IMeterAppointmentEvent {
  type: string
  status: string
  createdAt: string
  updatedAt: string
  action: string
  data: string
}

export interface IMeterAppointmentComment {
  comment: string
  createdAt: string
  updatedAt: string
}

export enum MeterAppointmentStatus {
  BOOKED = "BOOKED",
  RESCHEDULED = "RESCHEDULED",
  CANCELLED = "CANCELLED",
  ABORTED = "ABORTED",
  COMPLETED = "COMPLETED",
}

export const meterTypesText: Record<string, string>[] = [
  { text: "Electricity", value: "ELEC_ONLY" },
  { text: "Gas", value: "GAS_ONLY" },
  { text: "Dual Fuel", value: "DUAL" },
]

export const installationTypesText: Record<string, string>[] = [
  { text: "Traditional Meters", value: "TRADITIONAL" },
  { text: "Smart Meters (S1)", value: "SMETS1" },
  { text: "Smart Meters", value: "SMETS2" },
]

export const appointmentTypesText: Record<string, string>[] = [
  { text: "New Connection", value: "NEW_CONNECTION" },
  { text: "Scheduled Exchange", value: "METER_EXCHANGE" },
  { text: "Warrant", value: "WARRANT" },
  { text: "Recommission", value: "COMMISSION_JOIN" },
  { text: "Fault Investigation", value: "FAULTS" },
  { text: "Emergency Fault Investigation", value: "EMERGENCY_FAULTS" },
  { text: "Emergency Exchange", value: "EMERGENCY_METER_EXCHANGE" },
  { text: "Warrant Exchange", value: "WARRANT_METER_EXCHANGE" },
  { text: "Meter Removal", value: "METER_REMOVAL" },
  { text: "Warrant Removal", value: "WARRANT_REMOVAL" },
  { text: "Scheduled Site Investigation", value: "INVESTIGATION" },
  { text: "Emergency Site Investigation", value: "EMERGENCY_INVESTIGATION" },
]

export interface IMeterAppointmentStatus {
  status: MeterAppointmentStatus
}

export interface IAbortReasonEntry {
  question: string
  answer: string
}
